import React, { useCallback, useEffect, useMemo } from 'react';
import { Font } from 'api';
import { FormatXlsxDate } from 'components/SpreadsheetNotes/utilities/FormatXLSXDate/FormatXlsxDate';
import { FormatXlsxNumber } from 'components/SpreadsheetNotes/utilities/FormatXlsxNumber/FormatXlsxNumber';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';

// Helper: parse openpyxl-style color strings into CSS color
const parseExcelColorToCss = (color: unknown): string | undefined => {
  if (typeof color !== 'string' || !color) {
    return undefined;
  }
  // Ignore known backend error string
  if (color.includes('Values must be of type <class \'str\'>')) {
    return undefined;
  }
  // Theme-based color not currently resolvable here
  if (color.startsWith('theme:')) {
    return undefined;
  }

  const hex = color.replace(/^#/, '').toUpperCase();
  // ARGB (8 hex) or RGB (6 hex)
  if (/^[0-9A-F]{8}$/.test(hex)) {
    const a = parseInt(hex.substring(0, 2), 16) / 255;
    const r = parseInt(hex.substring(2, 4), 16);
    const g = parseInt(hex.substring(4, 6), 16);
    const b = parseInt(hex.substring(6, 8), 16);
    return `rgba(${r}, ${g}, ${b}, ${Number.isFinite(a) ? a.toFixed(3) : 1})`;
  }
  if (/^[0-9A-F]{6}$/.test(hex)) {
    return `#${hex}`;
  }
  // Sometimes Excel may send 3- or 4-length hex; attempt to normalize 3-digit RGB
  if (/^[0-9A-F]{3}$/.test(hex)) {
    const r = hex[0];
    const g = hex[1];
    const b = hex[2];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return undefined;
};

// Build inline CSS style from excel font object
const buildFontStyle = (font: Font): React.CSSProperties => {
  if (!font || typeof font !== 'object') {
    return {};
  }
  const style: React.CSSProperties = {};
  // Bold/Italic
  style.fontWeight = font.b ? 'bold' : undefined;
  style.fontStyle = font.i ? 'italic' : undefined;
  // Family
  /* this shouldn't throw an error if we don't support the font, but we could validate it against websafeFonts, if we want, or provide a back up */
  if (font.name) {
    style.fontFamily = font.name;
  }
  // Size is in points from Excel; CSS supports pt units directly
  if (font.size) {
    style.fontSize = `${font.size}pt`;
  }
  // Color
  const cssColor = parseExcelColorToCss(font.color);
  if (cssColor) {
    style.color = cssColor;
  }
  return style;
};

// Match FastValueViewer's props exactly
interface FastValueViewerProps {
  value?: any;
  cell: ICell;
  selected?: boolean;
  onDoubleClick?: (event: React.MouseEvent) => void;
  setDoubleClicked?: (value: boolean) => void;
}

const ExcelFormattedValueViewer = ({
  cell,
  selected,
  onDoubleClick,
  setDoubleClicked,
  value: cellValue,
}: FastValueViewerProps) => {
  const keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key || (event as unknown as { keyCode?: string | number }).keyCode;
      if (selected && key && key.toString().toUpperCase() === 'F2') {
        setDoubleClicked?.(true);
        onDoubleClick?.(event as unknown as React.MouseEvent);
      }
    },
    [onDoubleClick, selected, setDoubleClicked]
  );

  useEffect(() => {
    document.addEventListener('keyup', keyUpHandler);
    return () => document.removeEventListener('keyup', keyUpHandler);
  }, [keyUpHandler]);

  const spanId = useMemo(() => {
    if (cell?.extraAttr) {
      return `${cell.extraAttr}-${cell.alias}-${cell.key}_viewer`;
    }
    return `${cell?.alias}-${cell?.key}_viewer`;
  }, [cell]);

  const display = useMemo(() => {
    try {
      const excelProps = cell?.excelProps;
      if (!excelProps) {
        return String(cellValue ?? cell?.value ?? '');
      }
      const { format, dataType } = excelProps;

      const raw = cellValue ?? cell?.value;
      const num = typeof raw === 'number' ? raw : Number(raw);

      if (dataType === 'd' && format) {
        return FormatXlsxDate({ value: raw, format });
      }
      if (format && Number.isFinite(num) && raw !== '' && raw != null) {
        return FormatXlsxNumber({ value: num, format });
      }

      // Fallback to plain string if no excel format or non-numeric
      return raw != null ? String(raw) : '';
    } catch (_e) {
      return String(cellValue ?? cell?.value ?? '');
    }
  }, [cell, cellValue]);

  const textStyle = useMemo<React.CSSProperties>(() => {
    const font = (cell?.excelProps as any)?.font;
    const alignment = cell?.excelProps?.alignment;
    return {
      ...buildFontStyle(font),
      ...(alignment && typeof alignment.wrap_text === 'boolean'
        ? { whiteSpace: alignment.wrap_text ? ('normal' as const) : ('nowrap' as const) }
        : {}),
    };
  }, [cell]);

  const cellStyle = useMemo<React.CSSProperties>(() => {
    const style: React.CSSProperties = {};
    if (cell.excelProps?.alignment?.horizontal) {
      style.textAlign = cell.excelProps.alignment.horizontal as React.CSSProperties['textAlign'];
      style.display = 'block';
    }
    if (cell.excelProps?.dataType === 's') {
      if (!cell.excelProps.alignment?.horizontal) {
        style.textAlign = 'left';
        style.display = 'block';
      }
    }
    return style;
  }, [cell]);

  return (
    <span className="value-viewer" style={cellStyle}>
      <span id={spanId} className={`text-cell ${cell?.className ?? ''}`} onDoubleClick={onDoubleClick} style={textStyle}>
        {display}
      </span>
    </span>
  );
};

export default ExcelFormattedValueViewer;
